import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useFavorites, usePendingRemovals } from '../../../stores/appStore';
import { useSmartFavoriteEvents } from '../../../hooks/useSmartFavoriteEvents';
import { useWouterRouter } from '../../../hooks/useWouterNavigation';
import { Event } from '../../../types/event.types';
import { Icon } from '../../../pages/DesignSystem/primitives/Icon';
import Button from '../../../pages/DesignSystem/primitives/Button';
import EventCardCompact from '../../event/EventCardCompact';
import './FavoritesSidebar.css';

const UNDO_DELAY_MS = 6000;

interface FavoritesSidebarProps {
  onEventClick?: (event: Event, seriesEvents?: Event[]) => void;
}

const FavoritesSidebar: React.FC<FavoritesSidebarProps> = ({
  onEventClick
}) => {
  const { navigate } = useWouterRouter();
  const { favoriteEvents: storeEvents } = useFavorites();
  const { pendingRemovals, undoPendingRemoval } = usePendingRemovals();

  // Smart cache pour les favoris enrichis
  const { events: smartEvents, loading } = useSmartFavoriteEvents();

  // IDs en pending removal (pour les cacher visuellement)
  const pendingIds = useMemo(() => new Set(
    Object.values(pendingRemovals)
      .filter(r => r.status !== 'restoring')
      .map(r => r.id)
  ), [pendingRemovals]);

  // Utiliser les events du smart cache si disponibles
  const favoriteEvents = smartEvents.length > 0 ? smartEvents : storeEvents;

  // Grouper les événements récurrents
  const groupedFavorites = useMemo(() => {
    const single: Event[] = [];
    const recurring: Record<string, { mainEvent: Event; events: Event[] }> = {};

    favoriteEvents.forEach(event => {
      if (event.seriesId) {
        if (!recurring[event.seriesId]) {
          recurring[event.seriesId] = {
            mainEvent: event,
            events: []
          };
        }
        recurring[event.seriesId].events.push(event);
      } else {
        single.push(event);
      }
    });

    return { single, recurring };
  }, [favoriteEvents]);

  // Créer une liste unifiée avec les events et les undo rows à leur position originale
  type ListItem =
    | { type: 'recurring'; seriesId: string; group: { mainEvent: Event; events: Event[] } }
    | { type: 'single'; event: Event }
    | { type: 'undo'; removal: typeof pendingRemovals[string] };

  const unifiedList = useMemo(() => {
    // 1. Construire la liste des items visibles (sans les pending)
    const visibleItems: ListItem[] = [];

    // Recurring events d'abord
    Object.entries(groupedFavorites.recurring)
      .filter(([seriesId]) => !pendingIds.has(seriesId))
      .forEach(([seriesId, group]) => {
        visibleItems.push({ type: 'recurring', seriesId, group });
      });

    // Puis les single events
    groupedFavorites.single
      .filter(event => !pendingIds.has(event.id))
      .forEach(event => {
        visibleItems.push({ type: 'single', event });
      });

    // 2. Récupérer les pending removals triés par originalIndex (ordre croissant)
    const pendingList = Object.values(pendingRemovals)
      .filter(r => r.status !== 'restoring')
      .sort((a, b) => a.originalIndex - b.originalIndex);

    // 3. Insérer les undo rows à leur position originale (avec splice)
    const result: ListItem[] = [...visibleItems];
    pendingList.forEach(removal => {
      // Limiter l'index pour éviter les insertions hors limites
      const insertIndex = Math.min(removal.originalIndex, result.length);
      result.splice(insertIndex, 0, { type: 'undo', removal });
    });

    return result;
  }, [groupedFavorites, pendingIds, pendingRemovals]);

  // Compteur total (events visibles seulement, sans les pending)
  const totalCount = useMemo(() => {
    const visibleSingle = groupedFavorites.single.filter(e => !pendingIds.has(e.id)).length;
    const visibleRecurring = Object.keys(groupedFavorites.recurring).filter(id => !pendingIds.has(id)).length;
    return visibleSingle + visibleRecurring;
  }, [groupedFavorites, pendingIds]);

  // Nombre de pending removals actifs
  const pendingCount = useMemo(() => {
    return Object.values(pendingRemovals).filter(r => r.status !== 'restoring').length;
  }, [pendingRemovals]);

  // Afficher la liste si on a des events OU des pending removals
  const hasContent = totalCount > 0 || pendingCount > 0;

  // Handler pour clic sur event
  const handleEventClick = useCallback((event: Event, seriesEvents?: Event[]) => {
    if (onEventClick) {
      onEventClick(event, seriesEvents);
      return;
    }

    // Navigation par défaut
    if (event.seriesId) {
      navigate(`/series/${event.seriesId}`, {
        state: {
          event,
          seriesEvents,
          returnTo: window.location.pathname
        }
      });
    } else {
      navigate(`/event/${event.id}`, {
        state: {
          event,
          returnTo: window.location.pathname
        }
      });
    }
  }, [navigate, onEventClick]);

  return (
    <div className="favorites-sidebar">
      {/* Header */}
      <div className="favorites-sidebar__header">
        <Icon name="Heart" size={20} className="favorites-sidebar__icon" />
        <h2 className="favorites-sidebar__title">My Favorites</h2>
        {totalCount > 0 && (
          <span className="favorites-sidebar__count">{totalCount}</span>
        )}
      </div>

      {/* Content */}
      <div className="favorites-sidebar__content">
        {/* Ne montrer le loading que si on n'a pas encore de contenu (chargement initial) */}
        {loading && !hasContent && unifiedList.length === 0 ? (
          <div className="favorites-sidebar__loading">
            <div className="favorites-sidebar__loading-spinner" />
            <span>Loading favorites...</span>
          </div>
        ) : !hasContent ? (
          <div className="favorites-sidebar__empty">
            <Icon name="Heart" size={48} className="favorites-sidebar__empty-icon" />
            <p className="favorites-sidebar__empty-text">
              No favorites yet!
            </p>
            <p className="favorites-sidebar__empty-hint">
              Swipe right on events to add them to your favorites
            </p>
          </div>
        ) : (
          <div className="favorites-sidebar__list">
            {unifiedList.map((item, index) => {
              if (item.type === 'undo') {
                return (
                  <UndoRowCompact
                    key={`undo-${item.removal.id}`}
                    eventTitle={item.removal.eventTitle}
                    startTime={item.removal.startTime}
                    onUndo={() => undoPendingRemoval(item.removal.id)}
                  />
                );
              }
              if (item.type === 'recurring') {
                return (
                  <EventCardCompact
                    key={item.seriesId}
                    event={item.group.mainEvent}
                    seriesCount={item.group.events.length}
                    onClick={() => handleEventClick(item.group.mainEvent, item.group.events)}
                    showFavoriteButton={true}
                    listIndex={index}
                  />
                );
              }
              // type === 'single'
              return (
                <EventCardCompact
                  key={item.event.id}
                  event={item.event}
                  onClick={() => handleEventClick(item.event)}
                  showFavoriteButton={true}
                  listIndex={index}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {hasContent && (
        <div className="favorites-sidebar__footer">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/favorites')}
            className="favorites-sidebar__view-all"
          >
            <Icon name="ExternalLink" size={16} />
            View all favorites
          </Button>
        </div>
      )}
    </div>
  );
};

// Composant UndoRow compact pour la sidebar (memoized pour éviter re-renders)
interface UndoRowCompactProps {
  eventTitle: string;
  startTime: number;
  onUndo: () => void;
}

const UndoRowCompact: React.FC<UndoRowCompactProps> = React.memo(({ eventTitle, startTime, onUndo }) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / UNDO_DELAY_MS) * 100);
      setProgress(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 50);
    return () => clearInterval(interval);
  }, [startTime]);

  return (
    <div className="favorites-sidebar__undo-row">
      <div className="favorites-sidebar__undo-progress" style={{ width: `${progress}%` }} />
      <div className="favorites-sidebar__undo-content">
        <span className="favorites-sidebar__undo-text">"{eventTitle}" removed</span>
        <button className="favorites-sidebar__undo-button" onClick={onUndo}>
          Undo
        </button>
      </div>
    </div>
  );
});

export default FavoritesSidebar;
